import React from "react";
import { LayoutGrid, List } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import type { ViewMode } from "@/hooks/useViewPreference";

interface ViewToggleProps {
  value: ViewMode;
  onChange: (value: ViewMode) => void;
  className?: string;
}

/**
 * ViewToggle - Toggle between grid and list view modes
 * 
 * Features:
 * - Consistent toggle UI across all list pages
 * - Grid and List icons for clear visual indication
 * - Works with useViewPreference hook for persistence
 * 
 * @example
 * const { viewMode, setViewMode } = useViewPreference({ entityType: "projects" });
 * 
 * <ViewToggle value={viewMode} onChange={setViewMode} />
 */
const ViewToggle: React.FC<ViewToggleProps> = ({ value, onChange, className }) => {
  return (
    <ToggleGroup
      type="single"
      value={value}
      onValueChange={(val) => {
        if (val) onChange(val as ViewMode);
      }}
      className={cn("border rounded-md hidden sm:flex", className)}
    >
      <ToggleGroupItem 
        value="grid" 
        aria-label="Grid view"
        className="h-9 w-9 p-0 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
      >
        <LayoutGrid className="h-4 w-4" />
      </ToggleGroupItem>
      <ToggleGroupItem 
        value="list" 
        aria-label="List view"
        className="h-9 w-9 p-0 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
      >
        <List className="h-4 w-4" />
      </ToggleGroupItem>
    </ToggleGroup>
  );
};

export default ViewToggle;
